import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Chip, CircularProgress, Alert, Divider,
  List, ListItem, ListItemIcon, ListItemText, IconButton, Tooltip,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import MapIcon from '@mui/icons-material/Map';
import NearMeIcon from '@mui/icons-material/NearMe';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import apiClient from '../api/client';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

const numberedIcon = (n, color = '#1976d2') => L.divIcon({
  className: '',
  html: `<div style="background:${color};color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)">${n}</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

const startIcon = L.divIcon({
  className: '',
  html: `<div style="background:#2e7d32;color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🏠</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

const fmt = iso => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = d => d ? new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const PlanView = ({ planId, open, onClose }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !planId) return;
    setLoading(true);
    setError(null);
    apiClient.get(`/plans/${planId}`)
      .then(r => setPlan(r.data.data))
      .catch(e => setError(e.response?.data?.message || 'Failed to load plan'))
      .finally(() => setLoading(false));
  }, [open, planId]);

  const stops = plan?.stops?.slice().sort((a, b) => a.stop_order - b.stop_order) ?? [];

  const buildMapsUrl = (fromStopIndex) => {
    const origin = fromStopIndex === -1
      ? `${plan.start_lat},${plan.start_lng}`
      : `${stops[fromStopIndex].property.latitude},${stops[fromStopIndex].property.longitude}`;
    const remaining = fromStopIndex === -1 ? stops : stops.slice(fromStopIndex + 1);
    const destination = `${plan.start_lat},${plan.start_lng}`;
    const waypoints = remaining.map(s => `${s.property.latitude},${s.property.longitude}`).slice(0, 10).join('|');
    const params = new URLSearchParams({ api: '1', origin, destination, travelmode: 'driving' });
    if (waypoints) params.set('waypoints', waypoints);
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  };
  const startPos = plan ? [parseFloat(plan.start_lat), parseFloat(plan.start_lng)] : null;
  const stopPositions = stops
    .filter(s => s.property?.latitude && s.property?.longitude)
    .map(s => [parseFloat(s.property.latitude), parseFloat(s.property.longitude)]);
  const roadGeometry = plan?.route_geometry?.map(p => [p.lat, p.lng]) ?? null;
  const allPositions = startPos && stopPositions.length
    ? [startPos, ...stopPositions, startPos]
    : null;
  const mapCenter = startPos ?? [39.5, -98.35];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {loading ? 'Loading…' : plan?.name ?? 'Plan'}
        {plan && (
          <Typography variant="body2" color="text.secondary">{fmtDate(plan.plan_date)}</Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {error && <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>}

        {plan && (
          <>
            {/* Summary */}
            <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" size="small" startIcon={<MapIcon />} onClick={() => window.open(buildMapsUrl(-1), '_blank')}>
                Send to Google Maps
              </Button>
            </Box>
            <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`${stops.length} stops`} color="primary" />
              <Chip label={`${plan.summary?.total_travel_minutes} min driving`} />
              <Chip label={`${plan.summary?.total_showing_minutes} min showing`} />
              <Chip label={`Done by ${fmt(plan.summary?.end_time)}`} color="success" />
              <Chip label={plan.routing_source} variant="outlined" size="small" />
              {plan.warnings?.length > 0 && <Chip label={`${plan.warnings.length} warning(s)`} color="warning" size="small" />}
            </Box>

            {plan.warnings?.map((w, i) => (
              <Alert severity="warning" key={i} sx={{ mx: 2, mb: 1 }}>
                <strong>{w.address}</strong> — {w.message}
              </Alert>
            ))}
            {plan.dropped?.length > 0 && (
              <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
                Dropped to meet deadline: {plan.dropped.map(p => p.address).join(', ')}
              </Alert>
            )}

            {/* Map */}
            {allPositions && (
              <Box sx={{ mx: 2, mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                <MapContainer center={mapCenter} zoom={13} style={{ height: 300 }}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {roadGeometry
                    ? <Polyline positions={roadGeometry} color="#1976d2" weight={4} />
                    : <Polyline positions={allPositions} color="#1976d2" weight={3} dashArray="6,4" />
                  }
                  <Marker position={startPos} icon={startIcon}>
                    <Popup><strong>Start</strong><br />{plan.start_address}</Popup>
                  </Marker>
                  {stops.filter(s => s.property?.latitude).map((stop, i) => (
                    <Marker
                      key={stop.id}
                      position={[parseFloat(stop.property.latitude), parseFloat(stop.property.longitude)]}
                      icon={numberedIcon(i + 1, stop.must_show ? '#ed6c02' : '#1976d2')}
                    >
                      <Popup>
                        <strong>{i + 1}. {stop.property.address}</strong><br />
                        Arrive {fmt(stop.arrival_time)} · {stop.duration_minutes} min
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </Box>
            )}

            <Divider />

            {/* Timeline */}
            <List dense sx={{ px: 1 }}>
              <ListItem secondaryAction={
                <Tooltip title="Start full route from here">
                  <IconButton size="small" onClick={() => window.open(buildMapsUrl(-1), '_blank')} sx={{ color: 'text.secondary' }}>
                    <MapIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '50%', background: '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🏠</Box>
                </ListItemIcon>
                <ListItemText
                  primary={plan.start_address || 'Start'}
                  secondary={`Depart ${fmt(plan.summary?.start_time ?? stops[0]?.arrival_time)}`}
                />
              </ListItem>

              {stops.map((stop, i) => (
                <React.Fragment key={stop.id}>
                  <ListItem sx={{ pl: '20px', py: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      ↓ {stop.travel_time_minutes} min · {stop.distance_km} km
                    </Typography>
                  </ListItem>
                  <ListItem secondaryAction={
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title="Go here (from current location)">
                        <IconButton size="small" onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${stop.property?.latitude},${stop.property?.longitude}&travelmode=driving`, '_blank')}>
                          <NearMeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Start full route from stop ${i + 1}`}>
                        <IconButton size="small" onClick={() => window.open(buildMapsUrl(i), '_blank')} sx={{ color: 'text.secondary' }}>
                          <MapIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ width: 26, height: 26, borderRadius: '50%', background: stop.must_show ? '#ed6c02' : '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 12 }}>{i + 1}</Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          {stop.property?.address}, {stop.property?.city}
                          {stop.must_show && <Chip label="must show" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />}
                          {stop.anchored && <Chip label="anchored" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />}
                          {stop.property?.mls_number && <Chip label={`MLS# ${stop.property.mls_number}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                        </Box>
                      }
                      secondary={`Arrive ${fmt(stop.arrival_time)} · ${stop.duration_minutes} min · Leave ${fmt(stop.departure_time)}`}
                    />
                  </ListItem>
                </React.Fragment>
              ))}

              {plan.summary && (
                <>
                  <ListItem sx={{ pl: '20px', py: 0 }}>
                    <Typography variant="caption" color="text.secondary">↓ return</Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ width: 26, height: 26, borderRadius: '50%', background: '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🏠</Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={plan.start_address || 'Start'}
                      secondary={`Return by ${fmt(plan.summary.end_time)}`}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanView;
